import Image from "next/image";
import {
  type ClipRotationAngle,
  type RenderConfigClip,
  clipRotationAngles,
} from "@memento/types/render-config";
import { useEditor } from "src/components/Renderer/RendererProvider";
import { SegmentedToggle } from "src/components/Renderer/SegmentedToggle";

type RotationProps = {
  src: string;
  alt: string;
};

const rotationProps: Record<ClipRotationAngle, RotationProps> = {
  0: {
    src: "/images/icons/icnNoRotate.svg",
    alt: "unrotated square",
  },
  90: {
    src: "/images/icons/icnRotate90.svg",
    alt: "a square with an arrow indicating 90 degrees of rotation",
  },
  180: {
    src: "/images/icons/icnRotate180.svg",
    alt: "a square with an arrow indicating 180 degrees of rotation",
  },
  270: {
    src: "/images/icons/icnRotate270.svg",
    alt: "a square with an arrow indicating 270 degrees of rotation",
  },
};

type EditClipRotationProps = {
  clip: RenderConfigClip;
};

export const EditClipRotation = ({ clip }: EditClipRotationProps) => {
  const { setClipRotation, setFrameToSeek, clipPrefetchDetailsBySegmentId } =
    useEditor();

  const skipToFrame =
    clipPrefetchDetailsBySegmentId[clip.segmentId]?.skipToFrame ?? 0;

  return (
    <SegmentedToggle
      options={clipRotationAngles.map((rotation) => ({
        name: (
          <Image
            src={rotationProps[rotation].src}
            style={{
              transition: "filter 150ms ease-in-out",
              filter:
                clip.rotateAngleDegrees === rotation
                  ? "invert(0)"
                  : "invert(1)",
            }}
            alt={rotationProps[rotation].alt}
            width={24}
            height={24}
          />
        ),
        value: rotation,
      }))}
      selectedValue={clip.rotateAngleDegrees}
      onChange={(value) => {
        setClipRotation(clip.segmentId, value);
        setFrameToSeek({ frame: skipToFrame });
      }}
    />
  );
};
